import React, { useEffect, useState } from 'react';
import {
  Button, Col, Container, Row, Stack,
} from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import enAU from 'date-fns/locale/en-AU/index';
import './react-datepicker.css';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { selectSignUp, setPreferredDate } from '../features/signup/signupSlice';
import {
  earliestConnectionDate,
  isSelectableConnectionDate,
  latestConnectionDate,
  toIsoDay,
} from '../lib/connectionDate';
import { CF_CONNECTION_DATE_COPY } from '../lib/communityFibreCopy';

registerLocale('en-AU', enAU);

export interface ConnectionDateProps {
  onSubmit: () => void;
  previousStep: () => void;
}

/**
 * The Community Fibre installation step. Community Fibre's crew do the work
 * and book the visit with the customer themselves, so there is no installer
 * slot to book here: the customer picks the day they would like to be
 * connected and that is all the order carries.
 */
const ConnectionDate = ({ onSubmit, previousStep }: ConnectionDateProps) => {
  const dispatch = useAppDispatch();
  const signup = useAppSelector(selectSignUp);
  const earliest = earliestConnectionDate();
  const [chosen, setChosen] = useState<Date | null>(
    signup.preferredDate ? new Date(`${signup.preferredDate}T00:00:00`) : earliest,
  );

  // The earliest day is a sensible default, so the step can be taken without
  // touching the calendar at all.
  useEffect(() => {
    if (chosen && !signup.preferredDate) {
      dispatch(setPreferredDate(toIsoDay(chosen)));
    }
  }, [chosen, dispatch, signup.preferredDate]);

  const onPick = (date: Date | null) => {
    setChosen(date);
    if (date) {
      dispatch(setPreferredDate(toIsoDay(date)));
    }
  };

  return (
    <Container className="pineapple-form">
      <Row className="justify-content-center text-center mt-2">
        <Col md={8}>
          <h1 className="fw-bold text-primary text-center">
            Select your preferred installation date
          </h1>
          <p className="text-muted">{CF_CONNECTION_DATE_COPY}</p>
          <p className="fw-bold">
            Please select your preferred installation date below.
          </p>
        </Col>
      </Row>
      <Row className="justify-content-center mb-3">
        <Col xs="auto">
          <DatePicker
            selected={chosen}
            onChange={onPick}
            minDate={earliest}
            maxDate={latestConnectionDate()}
            filterDate={(date: Date) => isSelectableConnectionDate(date)}
            monthsShown={2}
            locale="en-AU"
            calendarStartDay={1}
            inline
          />
        </Col>
      </Row>
      <Stack
        gap={2}
        className="justify-content-center my-4"
        direction="horizontal"
      >
        <Button
          id="connection_date_go_back"
          onClick={previousStep}
          size="lg"
          variant="outline-primary"
        >
          <span className="px-2">Go back</span>
        </Button>
        <Button
          id="connection_date_continue"
          onClick={onSubmit}
          disabled={!chosen}
          size="lg"
          variant="primary"
        >
          <span className="px-2">Continue</span>
        </Button>
      </Stack>
    </Container>
  );
};

export default ConnectionDate;
